import axios from 'axios';
import axiosInstance from './AxiosService';
import AuthService from './AuthService';

// Helper function for authentication headers
const getAuthHeader = () => {
  const token = AuthService.getToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const uarService = {

  fetchUAR: async () => {
    const response = await axiosInstance.get('useraccessreview/');
    return response.data;
  },

  fetchUARById: async (uarID) => {
    const response = await axiosInstance.get(`useraccessreview/${uarID}/`);
    return response.data;
  },

  fetchUARByApp: async (APP_NAME) => {
    try {
      console.log(`Fetching UAR records for app: ${APP_NAME}`);
      // Try with a different URL format in case the backend expects it differently
      let response;
      try {
        // First, try the original endpoint format
        response = await axiosInstance.get(`useraccessreview/${APP_NAME}/`);
      } catch (firstError) {
        console.log("First attempt failed, trying alternative endpoint format");
        try {
          // Try with the APP_NAME as a query parameter
          response = await axiosInstance.get(`useraccessreview/?APP_NAME=${APP_NAME}`);
        } catch (secondError) {
          console.log("Second attempt failed, trying to fetch all UAR records and filter client-side");
          // As a last resort, get all records and filter on client side
          const allRecords = await axiosInstance.get('useraccessreview/');
          
          // Filter for records matching this app ID
          const filteredRecords = allRecords.data.filter(record => 
            record.APP_NAME === APP_NAME || 
            record.APP_ID === APP_NAME || 
            String(record.APP_NAME) === String(APP_NAME)
          );
          
          console.log(`Client-side filtering found ${filteredRecords.length} UAR records for app ${APP_NAME}`);
          return filteredRecords;
        }
      }
      
      console.log("UAR records fetched successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching UAR records for app:", APP_NAME);
      if (error.response) {
        // The request was made and the server responded with a status code outside of 2xx range
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
      } else if (error.request) {
        // The request was made but no response was received
        console.error("No response received:", error.request);
      } else {
        // Something happened in setting up the request
        console.error("Error message:", error.message);
      }
      // Return empty array instead of throwing error to avoid component breaking
      return [];
    }
  },

  fetchUARByUARFile: async (UAR_FILE) => {
    const response = await axiosInstance.get(`useraccessreviewsod/${UAR_FILE}/`);
    return response.data;
  },

  createUAR: async (uarData) => {
    try {
      console.log('UARService - Creating UAR with data:', JSON.stringify(uarData));
      
      // Create a sanitized copy of the data to send
      const sanitizedData = { ...uarData };
      
      // Remove any non-UUID APP_NAME if it's a string with special characters
      // The backend expects APP_NAME to be a UUID
      if (sanitizedData.APP_NAME && typeof sanitizedData.APP_NAME === 'string') {
        // If it doesn't look like a UUID (contains spaces or special chars), use APP_ID instead
        if (sanitizedData.APP_NAME.includes(' ') || 
            sanitizedData.APP_NAME.includes('-') && 
            !(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sanitizedData.APP_NAME))) {
          console.log('APP_NAME does not look like a valid UUID, using APP_ID instead');
          sanitizedData.APP_NAME = sanitizedData.APP_ID;
        }
      }
      
      // STRICT Validation and formatting of START_DATE to ensure YYYY-MM-DD format
      if (sanitizedData.START_DATE) {
        // Define regex for YYYY-MM-DD format
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        
        // If date doesn't match the exact format required
        if (!dateRegex.test(sanitizedData.START_DATE)) {
          console.warn('START_DATE not in YYYY-MM-DD format:', sanitizedData.START_DATE);
          
          // Try to parse and reformat various possible date inputs
          let formattedDate = null;
          
          try {
            // Try parsing with Date constructor
            const dateObj = new Date(sanitizedData.START_DATE);
            if (!isNaN(dateObj.getTime())) {
              const year = dateObj.getFullYear();
              const month = String(dateObj.getMonth() + 1).padStart(2, '0');
              const day = String(dateObj.getDate()).padStart(2, '0');
              formattedDate = `${year}-${month}-${day}`;
            }
          } catch (error) {
            console.error('Error parsing date:', error);
          }
          
          // If we have a valid formatted date, use it
          if (formattedDate && dateRegex.test(formattedDate)) {
            console.log('Successfully reformatted date to:', formattedDate);
            sanitizedData.START_DATE = formattedDate;
          } else {
            // If we couldn't format the date properly, throw an error
            throw new Error(`Invalid date format: ${sanitizedData.START_DATE}. Use YYYY-MM-DD format.`);
          }
        }
      } else {
        // START_DATE is required
        throw new Error('START_DATE is required');
      }
      
      console.log('UARService - Sending sanitized data:', JSON.stringify(sanitizedData));
      const response = await axiosInstance.post(`useraccessreview/`, sanitizedData);
      console.log('UARService - Create response:', response.data);
      return response.data;
    } catch (error) {
      // Check for specific error responses
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const { status, data } = error.response;
        
        console.error('Server error response:', status, data);
        
        if (status === 409) {
          // Conflict - record already exists
          console.warn('Conflict when creating UAR record:', data.detail);
          // Return the error message for handling in the component
          return { error: data.detail || 'A record with these details already exists' };
        }
        
        console.error('Error creating UAR record:', data);
        return { error: JSON.stringify(data) || 'Failed to create UAR record' };
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received when creating UAR record:', error.request);
        return { error: 'No response received from server' };
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up UAR creation request:', error.message);
        return { error: error.message || 'Unknown error occurred' };
      }
    }
  },

  updateUAR: async (uarID, uarData) => {
    const response = await axiosInstance.put(`useraccessreview/${uarID}/`, uarData);
    return response.data;
  },

  deleteUAR: async (uarID) => {
    const response = await axiosInstance.delete(`useraccessreview/${uarID}/`);
    return response.data;
  },

  //UARSODCHECK

  getUARSODProcessByAppCycle: async (APP_NAME, REVIEW_CYCLE) => {
    const response = await axiosInstance.get(`useraccessreviewsod/${APP_NAME}/${REVIEW_CYCLE}/`);
    return response.data;
  },

  createUARSODRecord: async (uarSODData) => {
    const response = await axiosInstance.post(`uarsod/`, uarSODData);
    return response.data;
  },

  updateUARSODRecord: async (recordID, uarSODData) => {
    const response = await axiosInstance.put(`uarsod/${recordID}/`, uarSODData);
    return response.data;
  },

  //Send UAR for Review
  sendUARForReview: async (uarSODData) => {
    const response = await axiosInstance.post(`send-uar-for-review/`, uarSODData);
    return response.data;
  },

    //Send UAR Follow-up
    sendUARFollowUp: async (uarSODData) => {
      const response = await axiosInstance.post(`follow-up-uar-for-review/`, uarSODData);
      return response.data;
    },

  fetchUARByToken: async (token) => {
    const response = await axiosInstance.get(`uartoken/${token}/`);
    return response.data;
  },

  // New method to fetch UAR_SOD_CHECK records by ROLE_OWNER
  fetchUARByRoleOwner: async (roleOwnerId) => {
    try {
      const response = await axiosInstance.get(`uarsod/role-owner/${roleOwnerId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching UAR records by role owner:', error);
      throw error;
    }
  },

  //CONFIGURE SCOPING 

  fetchInscopeRolesByUARFile: async (UAR_FILE) => {
    const response = await axiosInstance.get(`uarroleinscope/${UAR_FILE}/`);
    return response.data;
  },

  createInscopeRoles: async (uarRolesData) => {
    const response = await axiosInstance.post(`uarrolescoping/`, uarRolesData);
    return response.data;
  },

  updateInscopeRoles: async (APP_NAME, uarRolesData) => {
    const response = await axiosInstance.put(`uarrolescoping/${APP_NAME}/`, uarRolesData);
    return response.data;
  },

  deleteInscopeRoles: async (ROLE_ID) => {
    const response = await axiosInstance.delete(`uarrolescopingdelete/${ROLE_ID}/`);
    return response.data;
  },

  fetchUARByEmail: async (email) => {
    try {
      const response = await axiosInstance.get(`uar/email/${email}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching UAR by email:', error);
      throw error;
    }
  },

  // C&A Check methods
  fetchUARCACheck: async (uarFileId) => {
    try {
      const response = await axiosInstance.get(`uar/ca-check/${uarFileId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching UAR C&A check:', error);
      return null;
    }
  },

  createUARCACheck: async (data) => {
    try {
      const response = await axiosInstance.post(`uar/ca-check/`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating UAR C&A check:', error);
      throw error;
    }
  },

  updateUARCACheck: async (uarFileId, data) => {
    try {
      const response = await axiosInstance.put(`uar/ca-check/${uarFileId}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating UAR C&A check:', error);
      throw error;
    }
  },

  uploadUARCACheckEvidence: async (caCheckId, files, onUploadProgress) => {
    try {
      const formData = new FormData();
      
      // Add each file to form data
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      const response = await axiosInstance.post(
        `uar/ca-check/evidence/${caCheckId}/`, 
        formData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: onUploadProgress ? 
            (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onUploadProgress(percentCompleted);
            } : undefined
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading UAR C&A check evidence:', error);
      throw error;
    }
  },

  getUARCACheckEvidence: async (caCheckId) => {
    try {
      const response = await axiosInstance.get(`uar/ca-check/evidence/${caCheckId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching UAR C&A check evidence:', error);
      return [];
    }
  },

  deleteUARCACheckEvidence: async (evidenceId) => {
    try {
      const response = await axiosInstance.delete(`uar/ca-check/evidence/delete/${evidenceId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting UAR C&A check evidence:', error);
      throw error;
    }
  },

};

export default uarService;